import _ from 'lodash';
import React, { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  GetAnswerValue,
  GetDefaultValue,
  GetAnswerScoreValue,
  questionSchemaToArray,
} from '../../../../helpers/question-helper';
import {
  QuestionType,
  FieldSkipReason,
  SurveyQuestion,
  AttentionTextVariantEnum,
} from '../../../../services/api/api-client';
import { SurveyFillingComponentProps } from './surveyFilling';
import Style from './surveyFilling.module.scss';
import { AppTextField } from 'uikit/fields';
import { AppRadioGroupField } from 'uikit/fields';
import { AppSliderField } from 'uikit/fields';
import { SliderColorSchemeEnum } from 'uikit/inputs/slider/appSliderInput';
import { DateRangeComponent } from 'src/components/daterangePicker/daterange.component';
import { AppCheckboxGroupField } from 'uikit/fields/checkboxGroup/AppCheckboxGroupField.component';
import { Tooltip } from 'uikit/tooltip/tooltip.component';
import { ReactComponent as InfoIcon } from 'assets/img/common/f_info_circle_16.svg';

export const SurveyFillingComponent = (props: SurveyFillingComponentProps) => {
  const { schema: schemaWithAnswers, onChange, validationObject, onSkip, skippedQuestions, onAddRef } = props;
  const { t } = useTranslation();

  const renderQuestion = useCallback(
    (question: SurveyQuestion, index: string) => {
      switch (question.questionType) {
        case QuestionType.SingleChose:
        case QuestionType.YesNo:
        case QuestionType.Rate:
        case QuestionType.ConditionalQuestion:
          return (
            <AppRadioGroupField
              data-test-id={`question-control-${question.questionText}`}
              labelProps={{
                isBold: true,
                text: question.questionText,
                prefix: index,
                underLabelComponent: (
                  <SurveyAttentionText attentionText={question.attentionText} variant={question.attentionTextVariant} />
                ),
              }}
              isVertical={!question.isYesNoQuestion}
              options={question.options ?? []}
              labelField={'optionText'}
              onChange={(option) => onChange(question, option.optionValue)}
              valueField={'optionValue'}
              value={GetAnswerValue(question) ?? undefined}
              errorProps={{
                errors: Object.keys(validationObject).includes(question.id)
                  ? t('LocalErrors.FieldRequired')
                  : undefined,
              }}
              skipProps={{
                showReasons: false,
                inputCanBeSkipped: question.canBeSkipped,
                skipReason: skippedQuestions.includes(question.id) ? FieldSkipReason.NoReason : undefined,
                onSkipReasonChange: (reason) => onSkip(question, reason === FieldSkipReason.NoReason),
              }}
            />
          );

        case QuestionType.Scale:
          return (
            <AppSliderField
              labelProps={{
                isBold: true,
                text: question.questionText,
                prefix: index,
                underLabelComponent: (
                  <SurveyAttentionText attentionText={question.attentionText} variant={question.attentionTextVariant} />
                ),
              }}
              startWith={_.first(question.options)?.scoreValue ?? 0}
              endWith={_.last(question.options)?.scoreValue ?? 9}
              value={GetAnswerScoreValue(question)}
              startPoint={GetDefaultValue(question) === null ? undefined : Number(GetDefaultValue(question))}
              onChange={(val) => {
                const selectedOption = _.first(question.options.filter((o) => o.scoreValue === val));
                if (!selectedOption) return;
                onChange(question, selectedOption.optionValue);
              }}
              caption={{
                startText: _.first(question.options)?.optionText,
                endText: _.last(question.options)?.optionText,
              }}
              showMarks={true}
              colorScheme={
                question.firsOptionIsPositive ? SliderColorSchemeEnum.GreenToRed : SliderColorSchemeEnum.RedToGreen
              }
              errorProps={{
                errors: Object.keys(validationObject).includes(question.id)
                  ? t('LocalErrors.FieldRequired')
                  : undefined,
              }}
              skipProps={{
                showReasons: false,
                inputCanBeSkipped: question.canBeSkipped,
                skipReason: skippedQuestions.includes(question.id) ? FieldSkipReason.NoReason : undefined,
                onSkipReasonChange: (isSkipped) => onSkip(question, isSkipped === FieldSkipReason.NoReason),
              }}
            />
          );

        case QuestionType.FreeInput:
          return (
            <AppTextField
              type={'text-area'}
              labelProps={{
                isBold: true,
                text: question.questionText,
                prefix: index,
                underLabelComponent: (
                  <SurveyAttentionText attentionText={question.attentionText} variant={question.attentionTextVariant} />
                ),
              }}
              data-test-id={`question-control-${question.questionText}`}
              value={GetAnswerValue(question) ?? undefined}
              onChange={(e) => onChange(question, e.target.value)}
              errorProps={{
                errors: Object.keys(validationObject).includes(question.id)
                  ? t('LocalErrors.FieldRequired')
                  : undefined,
              }}
              skipProps={{
                showReasons: false,
                inputCanBeSkipped: question.canBeSkipped,
                skipReason: skippedQuestions.includes(question.id) ? FieldSkipReason.NoReason : undefined,
                onSkipReasonChange: (reason) => onSkip(question, reason === FieldSkipReason.NoReason),
              }}
            />
          );

        case QuestionType.MultipleChose:
          const options = question.options ?? [];
          return (
            <AppCheckboxGroupField
              data-test-id={`question-control-${question.questionText}`}
              labelProps={{
                isBold: true,
                text: question.questionText,
                prefix: index,
                underLabelComponent: (
                  <SurveyAttentionText attentionText={question.attentionText} variant={question.attentionTextVariant} />
                ),
              }}
              options={options}
              valueField={'optionValue'}
              labelField={'optionText'}
              value={options.filter((option) => option.answerValue != null).map((option) => option.optionValue)}
              onChange={(selectedOptionValues) => onChange(question, selectedOptionValues)}
              errorProps={{
                errors: Object.keys(validationObject).includes(question.id)
                  ? t('LocalErrors.FieldRequired')
                  : undefined,
              }}
              skipProps={{
                showReasons: false,
                inputCanBeSkipped: question.canBeSkipped,
                skipReason: skippedQuestions.includes(question.id) ? FieldSkipReason.NoReason : undefined,
                onSkipReasonChange: (reason) => onSkip(question, reason === FieldSkipReason.NoReason),
              }}
            />
          );

        case QuestionType.DateInput:
          return (
            <AppTextField
              type={'date'}
              labelProps={{
                isBold: true,
                text: question.questionText,
                prefix: index,
                underLabelComponent: (
                  <SurveyAttentionText attentionText={question.attentionText} variant={question.attentionTextVariant} />
                ),
              }}
              data-test-id={`question-control-${question.questionText}`}
              value={GetAnswerValue(question) ?? ''}
              onChange={(e) => onChange(question, e.target.value || null)}
              errorProps={{
                errors: Object.keys(validationObject).includes(question.id)
                  ? t('LocalErrors.FieldRequired')
                  : undefined,
              }}
              skipProps={{
                showReasons: false,
                inputCanBeSkipped: question.canBeSkipped,
                skipReason: skippedQuestions.includes(question.id) ? FieldSkipReason.NoReason : undefined,
                onSkipReasonChange: (reason) => onSkip(question, reason === FieldSkipReason.NoReason),
              }}
            />
          );

        case QuestionType.DateRangeInput:
          return (
            <DateRangeComponent
              isBold={true}
              prefix={index}
              label={question.questionText}
              underLabelComponent={
                <SurveyAttentionText attentionText={question.attentionText} variant={question.attentionTextVariant} />
              }
              startDate={question.options[0]?.answerValue ?? ''}
              endDate={question.options[1]?.answerValue ?? ''}
              onStartChange={(e) => onChange(question, [e.target.value, question.options[1]?.answerValue ?? ''])}
              onEndChange={(e) => onChange(question, [question.options[0]?.answerValue ?? '', e.target.value])}
              startDateErrorText={
                Object.keys(validationObject).includes(question.id) ? t('LocalErrors.FieldRequired') : undefined
              }
              endDateErrorText={
                Object.keys(validationObject).includes(question.id) ? t('LocalErrors.FieldRequired') : undefined
              }
              skipProps={{
                showReasons: false,
                inputCanBeSkipped: question.canBeSkipped,
                skipReason: skippedQuestions.includes(question.id) ? FieldSkipReason.NoReason : undefined,
                onSkipReasonChange: (reason) => onSkip(question, reason === FieldSkipReason.NoReason),
              }}
            />
          );

        case QuestionType.Content:
          return <SurveyContent question={question} prefix={index} />;

        default:
          return <p key={question.id}>{`${QuestionType[question.questionType].toString()} type is not supported`}</p>;
      }
    },
    [onChange, onSkip, skippedQuestions, t, validationObject],
  );

  return (
    <div className={Style.questionsContainer}>
      {schemaWithAnswers &&
        questionSchemaToArray(schemaWithAnswers)
          ?.filter((q) => !q.isSkippedRender)
          .map((q, i) => (
            <div
              key={q.id}
              ref={(el) => onAddRef(el, q.id)}
              className={Style.questionContainer}
              data-test-id={`survey-question-container-${i + 1}`}
            >
              <div data-test-id={'question'} className={Style.questionWrapper}>
                {renderQuestion(q, (i + 1).toString())}
              </div>
            </div>
          ))}
    </div>
  );
};

export const SurveyAttentionText: FC<{ attentionText?: string | null; variant?: AttentionTextVariantEnum | null }> = ({
  attentionText,
  variant = AttentionTextVariantEnum.Info,
}) => {
  if (!attentionText) return null;
  return (
    <div
      data-variant={AttentionTextVariantEnum[variant ?? AttentionTextVariantEnum.Info].toLowerCase()}
      className={Style.attentionText}
    >
      {attentionText}
    </div>
  );
};

const SurveyContent: FC<{ question: SurveyQuestion; prefix: string }> = ({ question, prefix }) => {
  const { t } = useTranslation();
  const firstContentBlock = question.contentBlock?.[0];
  if (!firstContentBlock) return <p>{'No content available'}</p>;

  const disclaimer = (
    <Tooltip
      hostStyles={Style.tooltipHost}
      text={t('Dashboard.PatientAccordion.SurveySection.SurveyFilling.InfoBlock')}
    >
      <InfoIcon className={Style.contentIcon} />
    </Tooltip>
  );

  return (
    <div className={Style.content} data-test-id={`question-control-${prefix}-${question.questionText}`}>
      <div className={Style.prefix}>{prefix}</div>
      <div>
        {firstContentBlock.titleText && (
          <div className={Style.contentHeader}>
            {disclaimer}
            <p className={Style.contentTitle}>{firstContentBlock.titleText}</p>
          </div>
        )}
        <div className={Style.contentBody}>
          {!firstContentBlock.titleText && disclaimer}
          <span className={Style.contentText}>{firstContentBlock.descriptionText}</span>
        </div>
      </div>
    </div>
  );
};
